import { useMemo, useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@marine/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@marine/ui/alert-dialog';
import { FeatureConfigSection } from '@/modules/platform/components/ai-settings/FeatureConfigSection';
import {
  usePlatformAIModels,
  useRefreshPlatformAIModels,
} from '@/modules/platform/hooks/usePlatformAIModels';
import { usePlatformAiFeatureConfigs } from '@/modules/platform/hooks/usePlatformAiFeatureConfigs';
import {
  aiFeatureConfigsApi,
  type PlatformAiFeatureConfig,
} from '@/modules/platform/services/aiFeatureConfigsApi';

interface FeatureMeta {
  feature:           string;
  label:             string;
  description:       string;
  filterVision:      boolean;
  hardcodedFallback: string;
}

// Ordering mirrors the migration seed order + the canonical FEATURES
// constant on the Eloquent model side. Keeps the UI stable across refetches.
const FEATURE_META: FeatureMeta[] = [
  {
    feature:           'description',
    label:             'Description generation',
    description:       'Free-form sales copy for inventory items (boat descriptions).',
    filterVision:      false,
    hardcodedFallback: 'anthropic/claude-sonnet-4.6',
  },
  {
    feature:           'spec-lookup.synthesis',
    label:             'Spec lookup (synthesis)',
    description:       'Structured boat specs from web search results. Search step uses Perplexity — unchanged.',
    filterVision:      false,
    hardcodedFallback: 'anthropic/claude-sonnet-4.6',
  },
  {
    feature:           'comparables',
    label:             'Comparable listings',
    description:       'Web-grounded search for comparable listings from marinas, dealers, and marketplaces. Feeds pricing comparisons and competitor analysis.',
    filterVision:      false,
    hardcodedFallback: 'anthropic/claude-sonnet-4.6',
  },
  {
    feature:           'competitor',
    label:             'Competitor comparison',
    description:       'Synthesis model for competitor comparison reports. Aggregates regional competitor listings into strengths/weaknesses/positioning analysis.',
    filterVision:      false,
    hardcodedFallback: 'anthropic/claude-sonnet-4.6',
  },
  {
    feature:           'location',
    label:             'Location description',
    description:       'Streaming text generation for location-specific descriptions (short + full). Used on the marina/harbor detail page and listing export.',
    filterVision:      false,
    hardcodedFallback: 'anthropic/claude-sonnet-4.6',
  },
  {
    feature:           'image-analysis',
    label:             'Image analysis',
    description:       'Vision model for extracting boat specs from uploaded photos. Multimodal agent runs during photo upload or on-demand.',
    filterVision:      true,
    hardcodedFallback: 'openai/gpt-4o',
  },
];

/**
 * Platform → AI Settings → Defaults tab (Phase 39.2 — CTRL-12).
 *
 * Renders 6 FeatureConfigSection rows + a "Copy to all features" affordance
 * at the top. Shared OpenRouter catalog fetch (one round-trip for all 6
 * dropdowns). Auto-save is silent 750 ms debounce per section; the dialog
 * below fires 5 PATCHes in parallel on confirm then invalidates the list
 * query so server truth refreshes all rows in one go.
 *
 * Tab-chrome lives in `PlatformAiSettingsPage.tsx`; this component is pure
 * content.
 */
export function DefaultsTab() {
  const { data: models, isLoading: isLoadingModels } = usePlatformAIModels();
  const refreshModels = useRefreshPlatformAIModels();
  const { data: configs, isLoading: isLoadingConfigs } = usePlatformAiFeatureConfigs();
  const qc = useQueryClient();

  const [copySourceFeature, setCopySourceFeature] = useState<string | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const configByFeature = useMemo(() => {
    const map = new Map<string, PlatformAiFeatureConfig>();
    (configs ?? []).forEach((c) => map.set(c.feature, c));
    return map;
  }, [configs]);

  const handleCopyToAll = async () => {
    if (!copySourceFeature) return;
    const source = configByFeature.get(copySourceFeature);
    if (!source) return;

    setIsCopying(true);
    try {
      const targets = FEATURE_META
        .filter((f) => f.feature !== copySourceFeature)
        .map((f) => f.feature);

      // Fire all PATCHes in parallel. On partial failure the invalidate below
      // will restore server truth to the cache.
      await Promise.allSettled(
        targets.map((feature) =>
          aiFeatureConfigsApi.update(feature, {
            model:       source.model,
            temperature: source.temperature,
            max_tokens:  source.max_tokens,
          }),
        ),
      );
      await qc.invalidateQueries({ queryKey: ['platform', 'ai', 'feature-configs'] });
    } finally {
      setIsCopying(false);
      setCopySourceFeature(null);
    }
  };

  if (isLoadingConfigs) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Copy-to-all affordance — admin picks a source, we replicate */}
      <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-3">
        <div className="text-xs text-muted-foreground">
          Copy one feature's model / temperature / max-tokens to all other features.
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Copy className="mr-2 h-3 w-3" />
              Copy to all features…
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Copy values across features</AlertDialogTitle>
              <AlertDialogDescription>
                Select a source feature. Its model, temperature, and max-tokens will overwrite the other 5 features.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              {FEATURE_META.map((f) => (
                <label
                  key={f.feature}
                  className="flex cursor-pointer items-center gap-2 rounded border border-border p-2 text-xs hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="copy-source"
                    value={f.feature}
                    checked={copySourceFeature === f.feature}
                    onChange={(e) => setCopySourceFeature(e.target.value)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isCopying}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleCopyToAll} disabled={isCopying || !copySourceFeature}>
                {isCopying ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                Copy to all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* 6 feature sections */}
      <div className="space-y-3">
        {FEATURE_META.map((meta) => {
          const config = configByFeature.get(meta.feature);
          if (!config) return null; // should never happen — 6 seeded rows guaranteed
          return (
            <FeatureConfigSection
              key={meta.feature}
              config={config}
              models={models}
              isLoadingModels={isLoadingModels}
              onRefreshModels={refreshModels.mutate}
              isRefreshingModels={refreshModels.isPending}
              filterVision={meta.filterVision}
              label={meta.label}
              description={meta.description}
              hardcodedFallback={meta.hardcodedFallback}
            />
          );
        })}
      </div>
    </div>
  );
}
